(function () {
  const SEL_ROOT = '[data-collection-infinite-scroll]';
  const SEL_GRID = '#main-collection-product-grid';
  const SEL_SENTINEL = '[data-collection-scroll-sentinel]';
  const SEL_LIVE = '[data-collection-scroll-live]';

  function buildFetchUrl(nextUrl, sectionId) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function parseSectionHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector(SEL_ROOT);
    const grid = doc.querySelector(SEL_GRID);
    return { root, grid };
  }

  class CollectionInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.grid = document.querySelector(SEL_GRID);
      this.sentinel = root.querySelector(SEL_SENTINEL);
      this.liveRegion = root.querySelector(SEL_LIVE);
      this.sectionId = root.dataset.sectionId;
      this.loading = false;
      this.observer = null;
    }

    init() {
      if (!this.sentinel || !this.grid || this.root.dataset.hasMore !== 'true') {
        return;
      }
      if (!this.root.dataset.nextUrl) {
        return;
      }
      this.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadNext();
            }
          });
        },
        { rootMargin: '0px 0px 320px 0px', threshold: 0 }
      );
      this.observer.observe(this.sentinel);
    }

    disconnect() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    async loadNext() {
      if (this.loading || this.root.dataset.hasMore !== 'true') return;
      const nextUrl = this.root.dataset.nextUrl;
      if (!nextUrl) return;

      // Stop observing until this request finishes so the sentinel cannot queue multiple loads.
      this.disconnect();
      this.loading = true;
      this.grid.setAttribute('aria-busy', 'true');
      // Avoid updating aria-live during fetch: some mobile browsers scroll to the live region.

      try {
        const fetchUrl = buildFetchUrl(nextUrl, this.sectionId);
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error('Network error');
        const html = await response.text();
        const { root: nextRoot, grid: nextGrid } = parseSectionHtml(html);

        if (nextGrid) {
          while (nextGrid.firstChild) {
            this.grid.appendChild(nextGrid.firstChild);
          }
        }

        document.querySelectorAll(`${SEL_GRID} template`).forEach((elm) => {
          elm.closest('form')?.append(elm.content.cloneNode(true));
        });

        if (nextRoot) {
          if (nextRoot.dataset.nextUrl) {
            this.root.dataset.nextUrl = nextRoot.dataset.nextUrl;
          } else {
            delete this.root.dataset.nextUrl;
          }
          this.root.dataset.hasMore = nextRoot.dataset.hasMore === 'true' ? 'true' : 'false';
        } else {
          delete this.root.dataset.nextUrl;
          this.root.dataset.hasMore = 'false';
        }

        if (this.liveRegion && window.collectionInfiniteScrollStrings?.loaded) {
          this.liveRegion.textContent = window.collectionInfiniteScrollStrings.loaded;
          setTimeout(function () {
            if (this.liveRegion) {
              this.liveRegion.textContent = '';
            }
          }.bind(this), 3000);
        }

        // Do not dispatch `filtered` here: FacetFiltersForm also listens for `filtered` and
        // remounts this module, which stacked extra IntersectionObservers and duplicated fetches.
      } catch (e) {
        if (this.liveRegion) {
          this.liveRegion.textContent = '';
        }
      } finally {
        this.grid.removeAttribute('aria-busy');
        this.loading = false;
        if (this.root.dataset.hasMore === 'true' && this.root.dataset.nextUrl) {
          this.init();
        } else {
          this.disconnect();
        }
      }
    }
  }

  let active = null;

  /**
   * @param {{ deferFirstPaint?: boolean }} [opts]
   * Defer the first IntersectionObserver until after paint + scroll restoration so mobile
   * Safari does not run loadNext() immediately (aria-live + DOM churn was jumping scroll to the grid footer).
   */
  function mount(opts) {
    const deferFirstPaint = opts && opts.deferFirstPaint === true;
    const root = document.querySelector(SEL_ROOT);
    if (!root) return;
    if (active) {
      active.disconnect();
    }
    active = new CollectionInfiniteScroll(root);
    const run = function () {
      active.init();
    };
    if (deferFirstPaint) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          setTimeout(run, 200);
        });
      });
    } else {
      run();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mount({ deferFirstPaint: true });
    });
  } else {
    mount({ deferFirstPaint: true });
  }

  window.addEventListener('filtered', function () {
    mount({ deferFirstPaint: false });
  });
})();
